import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import useAuth from "../useAuth";
import {
  ChevronLeft,
  ChevronRight,
  User,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Building2,
  Calendar,
  Wifi,
  X,
  Search,
} from "lucide-react";
import Skeleton from "../Components/SkeletonLoader";

export default function RegisteredWifiUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { token } = useAuth();

  // modal state for approving/editing a user
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const openApproveModal = (user) => {
    // make a shallow copy so editing doesn't mutate table directly
    setCurrentUser({ ...user });
    setIsModalOpen(true);
  };

  const fetchWifiUsers = async () => {
    setLoading(true);
    try {
      const params = {
        pageNo,
        pageSize,
        userWise: false,
      };

      if (debouncedSearch) {
        params.searchParams = debouncedSearch;
      }

      const response = await axios.get(`${server}/wifiusers/getAll`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setUsers(response.data.data);
        setTotalCount(response.data.count);
      } else {
        toast.error("Failed to fetch WiFi users");
      }
    } catch (error) {
      console.error("Error fetching WiFi users:", error);
      toast.error(error.response?.data?.message || "Error fetching WiFi users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPageNo(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (token) {
      fetchWifiUsers();
    }
  }, [pageNo, pageSize, debouncedSearch, token]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= Math.ceil(totalCount / pageSize)) {
      setPageNo(newPage);
    }
  };

  // This function is now used by form submission to send updated data
  const handleUpdateStatus = async (userData) => {
    try {
      // Deconstruct to separate the ID for the query param and remove extra fields from payload
      const { id, departmentName, createdAt, updatedAt, ...payload } = userData;

      const response = await axios.patch(
        `${server}/wifiusers/approveUser?wifiUserId=${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
          },
        },
      );

      if (response.status === 200) {
        toast.success(`User status updated to approved`);
        fetchWifiUsers(); // Refresh the list
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
      throw error;
    }
  };

  // form submission for modal
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!currentUser) return;
    try {
      await handleUpdateStatus(currentUser);
      setIsModalOpen(false);
      setCurrentUser(null);
    } catch (err) {
      // error handled in handleUpdateStatus
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && users.length === 0) {
    return (
      <div className="p-8 flex items-center justify-center text-gray-500">
        <div className="space-y-4 w-full max-w-6xl">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <Wifi className="h-6 w-6 text-blue-600" />
            Registered WiFi Users
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and view all registered WiFi users in the system.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative flex-1 md:flex-none md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white shadow-sm hover:border-gray-400 transition-colors"
            />
          </div>
          <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
              Total Users: {totalCount}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col relative min-h-[400px]">
        {/* Progress Bar for all loading states */}
        {loading && (
          <div className="absolute top-0 left-0 w-full h-1 z-20 bg-blue-50 overflow-hidden">
            <div className="h-full bg-blue-600 animate-progress origin-left"></div>
          </div>
        )}
        <style dangerouslySetInnerHTML={{
          __html: `
          @keyframes progress {
            0% { transform: scaleX(0); }
            50% { transform: scaleX(0.5); }
            100% { transform: scaleX(1); }
          }
          .animate-progress {
            animation: progress 1.5s infinite ease-in-out;
            width: 100%;
          }
        ` }} />
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-4">User Profile</th>
                <th className="px-6 py-4">Contact Details</th>
                <th className="px-6 py-4">Department</th>
                <th className="px-6 py-4">Policy</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Is Nodal</th>
                <th className="px-6 py-4">Registered Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-16 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="p-3 bg-gray-50 rounded-full">
                        <User className="h-6 w-6 text-gray-300" />
                      </div>
                      <span className="text-sm font-medium">
                        No WiFi users found
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="group hover:bg-blue-50/30 transition-colors"
                  >
                    {/* User Profile */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm">
                          {user.fullName
                            ? user.fullName.charAt(0).toUpperCase()
                            : "U"}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 text-sm">
                            {user.fullName || "Unknown User"}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <User className="h-3 w-3 text-gray-400" />
                            <span className="text-xs font-medium text-gray-500">
                              {user.username}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Contact Details */}
                    <td className="px-6 py-4">
                      <div className="space-y-1.5">
                        {user.phoneNumber && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <Phone className="h-3 w-3 text-gray-400" />
                            <span className="font-medium">
                              {user.phoneNumber}
                            </span>
                          </div>
                        )}
                        {user.email && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <Mail className="h-3 w-3 text-gray-400" />
                            <span className="font-medium truncate max-w-[200px]">
                              {user.email}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Department */}
                    <td className="px-6 py-4">
                      {user.departmentName || user.departmentId ? (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-blue-500" />
                            <span className="text-sm font-medium text-gray-700">
                              {user.departmentName || (user.id === 332 ? "Information Technology & e-Governance" : "Unknown Department")}
                            </span>
                          </div>
                          {user.departmentId && (
                            <div className="text-[10px] font-bold text-gray-400 ml-6 uppercase">
                              ID: {user.departmentId}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Policy */}
                    <td className="px-6 py-4">
                      {user.policiesMapperId ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                          {user.policiesMapperId}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2 items-start">
                        {/* Approval Status */}
                        {user.status && (
                          <div className="flex items-center gap-2">
                            <div
                              className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${user.status === "approved"
                                ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                                : user.status === "rejected"
                                  ? "text-red-700 bg-red-50 border-red-200"
                                  : "text-amber-700 bg-amber-50 border-amber-200"
                                }`}
                            >
                              {user.status === "approved" ? (
                                <CheckCircle className="h-3 w-3" />
                              ) : user.status === "rejected" ? (
                                <XCircle className="h-3 w-3" />
                              ) : (
                                <div className="h-2 w-2 rounded-full bg-amber-500" />
                              )}
                              <span className="capitalize">{user.status}</span>
                            </div>
                            {user.status === "pending" && (
                              <button
                                onClick={() =>
                                  openApproveModal({
                                    ...user,
                                    status: "approved",
                                  })
                                }
                                className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold rounded-lg transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center gap-1 shrink-0"
                                title="Approve User"
                              >
                                <CheckCircle className="h-3 w-3" />
                                Approve
                              </button>
                            )}
                          </div>
                        )}

                        {/* Verified Status */}
                        {user.isVerified ? (
                          <div className="flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                            <CheckCircle className="h-3 w-3 fill-blue-500 text-white" />
                            <span>Verified</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-200">
                            <XCircle className="h-3 w-3" />
                            <span>Unverified</span>
                          </div>
                        )}

                        {/* Deleted Status */}
                        {user.deleted && (
                          <div className="flex items-center gap-1.5 text-xs font-bold text-red-700 bg-red-50 px-2.5 py-1 rounded-full border border-red-200">
                            <XCircle className="h-3 w-3" />
                            <span>Deleted</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Is Nodal */}
                    <td className="px-6 py-4">
                      <div
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${user.isNodal
                          ? "text-indigo-700 bg-indigo-50 border-indigo-200"
                          : "text-gray-600 bg-gray-50 border-gray-200"
                          }`}
                      >
                        {user.isNodal ? (
                          <CheckCircle className="h-3 w-3 mr-1" />
                        ) : (
                          <XCircle className="h-3 w-3 mr-1" />
                        )}
                        {user.isNodal ? "Yes" : "No"}
                      </div>
                    </td>

                    {/* Registered Date */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="font-medium">
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleDateString()
                              : "—"}
                          </div>
                          <div className="text-xs text-gray-400">
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleTimeString(
                                [],
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )
                              : ""}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalCount > 0 && (
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-xs font-medium text-gray-500">
              Showing{" "}
              <span className="text-gray-900 font-bold">{users.length}</span> of{" "}
              <span className="text-gray-900 font-bold">{totalCount}</span> WiFi
              users
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pageNo - 1)}
                disabled={pageNo === 1}
                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="px-4 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-mono font-medium text-gray-700 shadow-sm">
                Page {pageNo} of {totalPages || 1}
              </div>
              <button
                onClick={() => handlePageChange(pageNo + 1)}
                disabled={pageNo === totalPages || totalPages === 0}
                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {isModalOpen && currentUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-gray-100 overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900">
                Approval Confirmation
              </h3>
              <button
                onClick={() => { setIsModalOpen(false); setCurrentUser(null); }}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center animate-pulse">
                  <CheckCircle className="h-8 w-8 text-emerald-600" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-xl font-bold text-gray-900">
                    Approve User?
                  </h4>
                  <p className="text-sm text-gray-500 max-w-[280px] mx-auto leading-relaxed">
                    Are you sure you want to approve{" "}
                    <span className="font-bold text-gray-900">
                      {currentUser.fullName || currentUser.username}
                    </span>
                    ? This will grant them full WiFi access immediately.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentUser(null);
                  }}
                  className="w-full sm:flex-1 px-4 py-2.5 bg-gray-100 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-200 transition-all border border-gray-200"
                >
                  No, Cancel
                </button>
                <button
                  onClick={() => handleSubmit()}
                  className="w-full sm:flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 hover:shadow-emerald-300 active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Yes, Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
